/**
 * What the task routes refuse, turned into something the operator can act on.
 *
 * `POST /admin/crm/tasks` and its PATCH answer 400 with a CODE and no sentence:
 * `milestone_project_mismatch` when the phase belongs to another project, and
 * `milestone_not_found` when the phase is gone. Both are reachable from a screen
 * that did everything right - the phase list is read once, when the project is
 * chosen, and somebody else can delete or move a milestone while the dialog sits
 * open - so a generic "something went wrong" would be the one answer that tells
 * the reader nothing about what to do next.
 *
 * `POST /admin/crm/tickets/:id/tasks` - the support desk filing a ticket as work
 * - answers with the same two codes and two more of its own: `project_not_found`
 * for a project deleted while its dialog was open, and `forbidden` for an
 * operator who may read the CRM but may not write to the task register. It maps
 * them HERE rather than in a mapper of its own, which is what this file's
 * existence is about: a second copy would drift the first time the server grew a
 * code, and the copy nobody updated keeps drawing the new refusal as a generic
 * failure.
 *
 * One home for the mapping rather than a ternary in the create dialog and a
 * second one in the facts card. Two copies would drift the moment the server
 * grew a third code, and the copy nobody updated would keep drawing the new
 * refusal as a generic failure - this repo has shipped exactly that shape
 * before, twice.
 *
 * Returns a catalogue KEY, never text: the caller holds `t` and decides where
 * the sentence lands (an inline strip inside the dialog, a toast beside a save
 * button), which is also what keeps this file free of React and of a locale.
 */
import { ApiError } from "../../../lib/api";

/**
 * Server code -> catalogue key.
 *
 * Anything not listed falls through to the generic failure on purpose: a code we
 * have never seen is a failure we cannot explain, and guessing at it would put a
 * confident wrong sentence on the screen.
 */
const KEYS: Record<string, string> = {
  milestone_project_mismatch: "tasks.milestoneMismatch",
  milestone_not_found: "tasks.milestoneGone",
  project_not_found: "tasks.projectGone",
  // A 403, not a 400, and the only entry here that is about the reader rather
  // than about the record. It reaches a screen when a right is taken away while
  // a session is open, or when a control that should have been hidden was not -
  // and "something went wrong" would send the operator looking for a fault in
  // their own data.
  forbidden: "tasks.noPermission",
};

export function taskErrorKey(err: unknown): string {
  // `ApiError` copies the server's `error` field into `message` (see lib/api),
  // which is where every other CRM screen reads a code off.
  const code = err instanceof ApiError ? err.message : "";
  return KEYS[code] ?? "common.error";
}
